import {
  Button,
  Modal,
} from '@mairistem/composants';
import {
  Exercice,
  ParametreGeneralEntite,
} from '@mairistem/comptabilite';
import { getErrorMessage } from '@mairistem/finances-utils';
import React from 'react';
import { toast } from 'react-toastify';
import { DropdownOption } from '@mairistem/liste';
import { isNil, map } from 'lodash';
import AUTO from '../../../../constants/common/common';
import { createBordereau, updateBordereau } from '../../../../services/piecesComptables/bordereaux/bordereaux';
import getSeries from '../../../../services/piecesComptables/series';
import type { BordereauWithState } from '../../../../interfaces/pieces/TreeRow';
import useModalSuperUserValidation from '../../../../hooks/useModalSuperUserValidation';
import BordereauForm from './BordereauForm';

interface BordereauModalProps {
  bordereau: BordereauWithState;
  isSuperUser?: boolean;
  dateDisabled?: boolean;
  entite?: ParametreGeneralEntite;
  exercice?: Exercice;
  isCreation: boolean;
  libelleDisabled?: boolean;
  numeroDisabled?: boolean;
  onClose: (data?: any) => void;
  onConfirm?: (data: BordereauWithState) => void;
  open: boolean;
  sensDisabled?: boolean;
  serieDisabled?: boolean;
  typeDisabled?: boolean;
}

const SENS_MUTATOR = { R: 'recette', D: 'dépense' } as const;

const TYPES_BORDEREAU = {
  '01': 'ordinaire',
  '02': "d'annulation-réduction",
  '03': "d'ordre de paiement",
  '04': 'de régularisation',
  RE: 'de rejet',
} as const;

const HORS_SERIE = {
  'data-testid': 'serieHorsSerie',
  key: 0,
  text: 'Hors-Série',
  value: 0,
} as const;

type CustomBordereauKeys = 'sens' |
'typePes' |
'numero' |
'dateEmission' |
'identifiantSerie' |
'libelle';

const BordereauModal = ({
  bordereau,
  isSuperUser = false,
  dateDisabled = false,
  entite,
  exercice,
  isCreation = false,
  libelleDisabled = false,
  numeroDisabled = false,
  onClose,
  onConfirm,
  open = false,
  sensDisabled = false,
  serieDisabled = false,
  typeDisabled = false,
}: BordereauModalProps) => {
  const [series, setSeries] = React.useState<DropdownOption[]>([]);
  const [loaded, setLoaded] = React.useState(false);
  const [customBordereau, setCustomBordereau] = React.useState<Pick<BordereauWithState, CustomBordereauKeys>>();

  const { isValidated, validationComponent } = useModalSuperUserValidation();

  const handleClick = async () => {
    if (!isNil(exercice?.identifiant)) {
      const body: BordereauWithState = {
        ...(customBordereau?.numero !== AUTO && { numero: customBordereau?.numero }),
        codeBHM: bordereau?.codeBHM,
        dateEmission: customBordereau?.dateEmission,
        identifiantEntite: entite?.identifiantEntite,
        identifiantExercice: exercice?.identifiant,
        identifiantSerie: customBordereau?.identifiantSerie === 0 ? null : customBordereau?.identifiantSerie,
        libelle: customBordereau?.libelle,
        sens: customBordereau?.sens,
        typePes: customBordereau?.typePes,
        alerteTraitee: bordereau?.alerteTraitee ?? false,
        acquitte: bordereau?.acquitte ?? 0,
        edite: bordereau?.edite ?? false,
        retourPesKo: bordereau?.retourPesKo ?? false,
        retourPesOk: bordereau?.retourPesOk ?? false,
      };

      if (isCreation) {
        try {
          const data = await createBordereau(body);
          onConfirm?.(data);
          onClose?.(data);
        } catch (error) {
          toast.error(getErrorMessage(error, 'Impossible de créer le bordereau'));
          onClose?.();
        }
      } else if (!isNil(bordereau?.identifiant)) {
        try {
          const data = await updateBordereau({
            ...body,
            identifiant: bordereau?.identifiant,
          }, new URLSearchParams({
            withJournaux: 'true',
          }));
          onConfirm?.(data);
          onClose?.(data);
        } catch (error) {
          toast.error(getErrorMessage(error, 'Impossible de modifier le bordereau'));
          onClose?.();
        }
      }
    }

    onClose(); // Fermer la modal après validation
  };

  const loadSeries = () => {
    if (!isNil(exercice?.identifiant) && !loaded) {
      getSeries(new URLSearchParams({ identifiantExercice: exercice?.identifiant?.toString() }))
        .then(({ data }) => {
          setSeries([
            HORS_SERIE,
            ...map(data, (serie) => ({
              'data-testid': `serie${serie.identifiant}`,
              key: serie?.identifiant,
              text: serie?.libelle,
              value: serie?.identifiant,
            })),
          ]);
          setLoaded(true);
        }).catch(() => {
          toast.error('Impossible de récupérer les séries');
        });
    }
  };

  return (
    <Modal
      closeIcon
      onClose={onClose}
      onMount={loadSeries}
      open={open}
      size="large"
    >
      <Modal.Header
        content={isCreation ? "Création d'un bordereau"
          : `Modification du bordereau
            ${TYPES_BORDEREAU[bordereau?.typePes] ?? ''} de 
            ${SENS_MUTATOR[bordereau?.sens] ?? ''} n°${bordereau?.numero ?? ''}
            ${exercice ? ` - Exercice ${exercice?.anneeExercice} - ${entite?.raisonSociale}` : ''}`}
      />
      <Modal.Content>
        <BordereauForm
          bordereau={bordereau}
          customBordereau={customBordereau}
          setCustomBordereau={setCustomBordereau}
          series={series}
          libelleDisabled={libelleDisabled}
          numeroDisabled={numeroDisabled}
          dateDisabled={dateDisabled}
          sensDisabled={sensDisabled}
          serieDisabled={serieDisabled}
          typeDisabled={typeDisabled}
        />
        {
          isSuperUser && validationComponent
        }
      </Modal.Content>
      <Modal.Actions>
        <Button
          content="Valider"
          data-testid="validerBordereau"
          onClick={handleClick}
          positive
          size="big"
          disabled={isSuperUser && !isValidated}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default BordereauModal;
